import React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";

// Pages
import Dashboard from "../pages/dashboard/Dashboard";
import Employees from "../pages/employees/Employees";
import WeekSchedule from "../pages/week-schedule/WeekSchedule";
import Requests from "../pages/week-schedule/Requests";
import HRForm from "../pages/hr-form/HRForm";
import Tickets from "../pages/tickets/Tickets";
import TicketDetails from "../pages/tickets/TicketDetails";
import GroupChat from "../pages/chat/GroupChat";
import AssignedToYou from "../pages/boards/AssignedToYou";
import TaskBoard from "../pages/task-board/TaskBoard";
import Projects from "../pages/task-board/Projects";
import ProjectDetails from "../pages/task-board/ProjectDetails";
import Squads from "../pages/task-board/Squads";
import SquadDetails from "../pages/task-board/SquadDetails";
import Sprints from "../pages/task-board/Sprints";
import SprintDetails from "../pages/task-board/SprintDetails";
import EmployeeRequests from "../pages/task-board/EmployeeRequests";
import Profile from "../pages/profile/Profile";
import NotificationCenter from "../pages/notifications/NotificationCenter";
import EmployeesMonthlyReport from "../pages/management/EmployeesMonthlyReport";
import SystemStatus from "../pages/system/SystemStatus";
import Expenses from "../pages/expenses/Expenses";
import MeetingNotesList from "../pages/meeting-notes/MeetingNotesList";
import MeetingNoteEditor from "../pages/meeting-notes/MeetingNoteEditor";
import Documentation from "../pages/documentation/Documentation";
import MyAttendance from "../pages/attendance/MyAttendance";
import AdminAttendance from "../pages/attendance/AdminAttendance";
import TeamPresence from "../pages/presence/TeamPresence";
import AdminMonitoring from "../pages/monitoring/AdminMonitoring";
import MonitoringSettings from "../pages/monitoring/MonitoringSettings";
import SessionDetail from "../pages/monitoring/SessionDetail";
import DevRules from "../pages/dev-rules/DevRules";
import AIStudio from "../pages/ai-studio/AIStudio";
import QAEngineer from "../pages/ai-studio/qa-engineer/QAEngineer";

// Components
import RequireAdmin from "../components/RequireAdmin";
import RequirePresenceAccess from "../components/RequirePresenceAccess";
import Unauthorized from "../components/Unauthorized";
import NotFound from "../components/NotFound";

// Redirect component for dynamic routes
const RedirectWithParams = ({ to }) => {
  const params = useParams();
  const newPath = Object.keys(params).reduce((path, key) => {
    return path.replace(`:${key}`, params[key]);
  }, to);
  return <Navigate to={newPath} replace />;
};

const Router = () => {
  return (
    <Routes>
      {/* Default redirect to dashboard */}
      <Route path="/" element={<Navigate to="/main/dashboard" replace />} />

      {/* Main Section */}
      <Route path="/main/dashboard" element={<Dashboard />} />
      <Route path="/main/week-schedule" element={<WeekSchedule />} />
      <Route path="/main/assigned-to-you" element={<AssignedToYou />} />
      <Route path="/main/documentation" element={<Documentation />} />
      <Route path="/main/dev-rules" element={<DevRules />} />
      <Route path="/ai-studio" element={<AIStudio />} />
      <Route path="/ai-studio/qa-engineer" element={<QAEngineer />} />

      {/* Redirects for old Main routes */}
      <Route
        path="/dashboard"
        element={<Navigate to="/main/dashboard" replace />}
      />
      <Route
        path="/week-schedule"
        element={<Navigate to="/main/week-schedule" replace />}
      />
      <Route
        path="/dev-rules"
        element={<Navigate to="/main/dev-rules" replace />}
      />
      <Route
        path="/boards/assigned-to-you"
        element={<Navigate to="/main/assigned-to-you" replace />}
      />

      {/* Management Section */}
      <Route
        path="/management/employees"
        element={
          <RequireAdmin>
            <Employees />
          </RequireAdmin>
        }
      />
      <Route path="/management/requests" element={<Requests />} />
      <Route
        path="/management/hr-form"
        element={
          <RequireAdmin>
            <HRForm />
          </RequireAdmin>
        }
      />
      <Route
        path="/management/monthly-report"
        element={<EmployeesMonthlyReport />}
      />
      <Route
        path="/management/system-status"
        element={
          <RequireAdmin>
            <SystemStatus />
          </RequireAdmin>
        }
      />

      {/* Communication Section */}
      <Route path="/communication/tickets" element={<Tickets />} />

      {/* Attendance */}
      <Route path="/management/attendance" element={<MyAttendance />} />
      <Route
        path="/team-presence"
        element={
          <RequirePresenceAccess>
            <TeamPresence />
          </RequirePresenceAccess>
        }
      />
      <Route
        path="/management/attendance/admin"
        element={
          <RequireAdmin>
            <AdminAttendance />
          </RequireAdmin>
        }
      />

      {/* Monitoring — admin only. Employees have no view of their own
          recordings or screenshots by design. */}
      <Route
        path="/management/monitoring"
        element={
          <RequireAdmin>
            <AdminMonitoring />
          </RequireAdmin>
        }
      />
      <Route
        path="/management/monitoring/settings"
        element={
          <RequireAdmin>
            <MonitoringSettings />
          </RequireAdmin>
        }
      />
      <Route
        path="/management/monitoring/sessions/:id"
        element={
          <RequireAdmin>
            <SessionDetail />
          </RequireAdmin>
        }
      />

      <Route path="/communication/tickets/:id" element={<TicketDetails />} />
      <Route path="/communication/chat" element={<GroupChat />} />
      <Route path="/communication/expenses" element={<Expenses />} />
      <Route
        path="/communication/meeting-notes"
        element={<MeetingNotesList />}
      />
      <Route
        path="/communication/meeting-notes/:id"
        element={<MeetingNoteEditor />}
      />

      {/* Notifications */}
      <Route path="/notifications" element={<NotificationCenter />} />

      {/* Redirects for old routes */}
      <Route
        path="/employees"
        element={<Navigate to="/management/employees" replace />}
      />
      <Route
        path="/requests"
        element={<Navigate to="/management/requests" replace />}
      />
      <Route
        path="/hr-form"
        element={<Navigate to="/management/hr-form" replace />}
      />
      <Route
        path="/system-status"
        element={<Navigate to="/management/system-status" replace />}
      />
      <Route
        path="/tickets"
        element={<Navigate to="/communication/tickets" replace />}
      />
      <Route
        path="/tickets/:id"
        element={<RedirectWithParams to="/communication/tickets/:id" />}
      />
      <Route
        path="/chat"
        element={<Navigate to="/communication/chat" replace />}
      />
      <Route
        path="/expenses"
        element={<Navigate to="/communication/expenses" replace />}
      />
      <Route
        path="/meeting-notes"
        element={<Navigate to="/communication/meeting-notes" replace />}
      />
      <Route
        path="/meeting-notes/:id"
        element={<RedirectWithParams to="/communication/meeting-notes/:id" />}
      />

      {/* Unified task board (replaces the per-team admin/frontend/backend/uiux/tester/mobile boards) */}
      <Route path="/task-board" element={<TaskBoard />} />
      <Route path="/task-board/projects" element={<Projects />} />
      <Route path="/task-board/projects/:id" element={<ProjectDetails />} />
      <Route path="/task-board/squads" element={<Squads />} />
      <Route path="/task-board/squads/:id" element={<SquadDetails />} />
      <Route path="/task-board/sprints" element={<Sprints />} />
      <Route path="/task-board/sprints/:id" element={<SprintDetails />} />
      {/* Everyone can view employee requests; only admins can act (enforced by the API too). */}
      <Route path="/task-board/employee-requests" element={<EmployeeRequests />} />
      {/* Old board URLs (e.g. links inside past notifications) land on the new board. */}
      <Route path="/boards/*" element={<Navigate to="/task-board" replace />} />

      {/* Profile */}
      <Route path="/profile" element={<Profile />} />

      {/* Unauthorized */}
      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* Logout (UI Only) */}
      <Route
        path="/logout"
        element={
          <div className="flex items-center justify-center h-screen">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-light-100 mb-4">
                Logging out...
              </h1>
              <p className="text-light-500">
                This is UI only - no actual logout logic
              </p>
            </div>
          </div>
        }
      />

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default Router;
